/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Form as FormikForm, Formik, yupToFormErrors, Field as FormikField } from 'formik'
import { Button, Form } from 'react-bootstrap'
import isEqual from 'lodash/isEqual'
import styled from 'styled-components'
import { authApiService, type UserSettings, type LanguageInfo } from '../../../services/api/auth'
import { User } from '../../../services/api/types'
import {
  getNotificationSettings,
  changeNotificationSettings,
  processNotificationSettingsForUI,
  transformNotificationSettingsForAPI,
  type NotificationSettingUI
} from '../../../services/api/notifications'
import { getDateFormats, getTimeFormats } from '../../../services/api/api.settings'
import { toast } from '../../../services/toast'
import { useServiceContext } from '../../../root'
import Loader from '../../../components/shared/Loader'
import { Select } from '../../../components/Select'
import ValidationSchema from './FormValidation'
import LoadingSpinner from '../../../components/LoadingSpinner'

const Table = styled.table`
  border: none !important;
  margin-bottom: 20px;
`

const InlineCheckboxWrapper = styled.div`
  float: left;
  width: 41px;
  padding-left: 8px;
  .checkbox {
    margin: 0 !important;
    height: 24px;
    font-weight: normal;
  }
`

interface SelectOption {
  value: string
  label: string
}

interface FormValues {
  userSettings: UserSettings
  notifSettings: NotificationSettingUI[]
}

// Transform array to react-select options
const transformToSelectOptions = (
  arr: any[],
  valueKey: string = 'id',
  labelKey: string = 'value',
  translateFn?: (key: string) => string
): SelectOption[] => {
  if (!Array.isArray(arr)) return []

  return arr.map((val) => {
    if (typeof val === 'string') {
      return { value: val, label: val }
    }

    const label = translateFn && val[labelKey] ? translateFn(val[labelKey]) : val[labelKey]
    return {
      value: val[valueKey],
      label: label || val[valueKey]
    }
  })
}

const SettingsForm: React.FC = () => {
  const { service } = useServiceContext()
  const translate = service.filter.translate
  const validationSchema = ValidationSchema()

  const [user, setUser] = useState<User | null>(null)
  const [userSettings, setUserSettings] = useState<UserSettings>({
    locale: '',
    dateFormat: '',
    timeFormat: '',
    timeFormat24h: false
  })

  const [notifSettings, setNotifSettings] = useState<NotificationSettingUI[]>([])
  const [blocked, setBlocked] = useState(true)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [languagesOptions, setLanguagesOptions] = useState<SelectOption[]>([])
  const [dateFormatsClientOptions, setDateFormatsClientOptions] = useState<SelectOption[]>([])
  const [timeFormatsClientOptions, setTimeFormatsClientOptions] = useState<SelectOption[]>([])
  const [policies, setPolicies] = useState<any>({})
  const isCheckmated = service.checkmate.isCheckmated()

  useEffect(() => {
    loadData()
  }, [])

  const getDefaults = (vals?: any) => {
    const defaults = validationSchema.cast(vals, {
      recursive: true,
      stripUnknown: true,
      context: vals
    })
    return defaults as FormValues
  }

  const isSaveBtnDisabled = (values: FormValues) => {
    return isEqual(values.userSettings, userSettings) && isEqual(values.notifSettings, notifSettings)
  }

  const save = async (values: FormValues) => {
    const promises: Promise<any>[] = []

    setBlocked(true)

    const userSettingsChanged = !isEqual(values.userSettings, userSettings)
    const notifSettingsChanged = !isEqual(values.notifSettings, notifSettings)

    if (userSettingsChanged) {
      promises.push(authApiService.changeUserSettings(values.userSettings))
    }

    if (notifSettingsChanged) {
      const apiPayload = transformNotificationSettingsForAPI(values.notifSettings)
      if (apiPayload.settings.length > 0) {
        promises.push(changeNotificationSettings(apiPayload))
      }
    }

    try {
      await Promise.all(promises)
      toast.success(translate('MY_SETTINGS_UPDATE_SUCCESS_MSG'))

      if (userSettingsChanged) {
        setUserSettings(values.userSettings)
      }

      if (notifSettingsChanged) {
        setNotifSettings(values.notifSettings)
      }

      // Handle language change if needed
      if (userSettingsChanged && values.userSettings.locale) {
        // TODO: Implement language change logic if available
        service.auth.changeLanguage(values.userSettings.locale)
      }
    } catch (error: any) {
      // ApiError has body property, not data
      let errorMsg = translate('ERROR')
      if (error?.body) {
        // Try different error message formats from API
        errorMsg = error.body.message || error.body.error || error.body.errorMessage || JSON.stringify(error.body)
      } else if (error?.message) {
        errorMsg = error.message
      }
      toast.error(errorMsg)
    } finally {
      setBlocked(false)
    }
  }

  const loadData = async () => {
    setBlocked(true)
    setIsInitialLoading(true)

    // Get date/time format options and translate them
    const dateFormats = getDateFormats()
    const timeFormats = getTimeFormats()

    setDateFormatsClientOptions(transformToSelectOptions(dateFormats, 'id', 'value', translate))
    setTimeFormatsClientOptions(transformToSelectOptions(timeFormats, 'id', 'value', translate))

    try {
      const [currentUserResponse, languages, policiesData, notificationSettings] = await Promise.all([
        authApiService.getCurrentLoggedInUser(),
        authApiService.getAllLanguages(),
        authApiService.getUserPolicies(),
        getNotificationSettings()
      ])

      setUser(currentUserResponse.user)
      setUserSettings({
        locale: currentUserResponse.user.locale || '',
        dateFormat: currentUserResponse.user.dateFormat || '',
        timeFormat: currentUserResponse.user.timeFormat || '',
        timeFormat24h: currentUserResponse.user.timeFormat24h || false
      })

      // Transform languages array - add 'value' property like old implementation
      const processedLanguages = languages
        .filter((lang: LanguageInfo) => lang.active)
        .map((lang: LanguageInfo) => ({
          ...lang,
          id: lang.localeTag,
          value: lang.nativeName !== lang.englishName ? `${lang.nativeName} / ${lang.englishName}` : lang.nativeName
        }))

      const langOptions = transformToSelectOptions(processedLanguages, 'localeTag', 'value')
      setLanguagesOptions(langOptions)

      setPolicies(policiesData)

      const processedNotifSettings = processNotificationSettingsForUI(notificationSettings, translate)
      setNotifSettings(processedNotifSettings)
    } catch (error) {
      toast.error(translate('ERROR'))
    } finally {
      setBlocked(false)
      setIsInitialLoading(false)
    }
  }

  const initialValues = getDefaults({
    userSettings: userSettings,
    notifSettings: notifSettings
  })

  //   Don't render form until data is loaded
  if (isInitialLoading) {
    return <LoadingSpinner size={40} centered={true} />
  }

  return (
    <Loader blocking={blocked}>
      <Formik
        validate={(values) => {
          return validationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {},
              (err: any) => yupToFormErrors(err)
            )
        }}
        onSubmit={(values) => {
          return save(values)
        }}
        enableReinitialize={true}
        initialValues={initialValues}
      >
        {({ values, setFieldValue, resetForm, handleSubmit }) => {
          return (
            <FormikForm>
              <Table className="my-rcm-settings-table">
                {!isCheckmated && (
                  <tbody>
                    <tr>
                      <td className="name">{translate('EMAIL_ADDRESS')}</td>
                      <td className="value">{user && user.email ? user.email : translate('NOT_AVAILABLE')}</td>
                    </tr>
                    <tr>
                      <td className="name">{translate('ROLE')}</td>
                      <td className="value">
                        {user && user.role ? translate('ROLE_' + user.role) : translate('NOT_AVAILABLE')}
                      </td>
                    </tr>
                    <tr>
                      <td className="name">{translate('TITLE_USER')}</td>
                      <td className="value">{user && user.title ? user.title : translate('NOT_AVAILABLE')}</td>
                    </tr>
                    <tr>
                      <td className="name">{translate('DEPARTMENT')}</td>
                      <td className="value">
                        {user && user.department ? user.department : translate('NOT_AVAILABLE')}
                      </td>
                    </tr>
                    <tr>
                      <td className="name">{translate('LANGUAGE')}</td>
                      <td className="value">
                        <Select
                          placeholder={translate('OPTION_SELECT_LANGUAGE')}
                          value={values.userSettings.locale}
                          options={languagesOptions}
                          onChange={(option: any) => {
                            setFieldValue('userSettings.locale', option ? option.value : null)
                          }}
                          isClearable={false}
                          id="36f21afc-d1a7-47b9-8e15-c32914d906c8"
                        />
                      </td>
                    </tr>
                  </tbody>
                )}
                <tbody>
                  <tr>
                    <td className="name" colSpan={2}>
                      {translate('SYSTEM_POLICIES_DATE_FORMAT_INTERFACE')}
                      <Select
                        placeholder={translate('SYSTEM_POLICIES_SELECT_DATE_FORMAT')}
                        className={'font-weight-normal mt5'}
                        isClearable={false}
                        isDisabled={!policies.allowPersonalizeClientDateformat}
                        value={values.userSettings.dateFormat}
                        options={dateFormatsClientOptions}
                        onChange={(option: any) => {
                          setFieldValue('userSettings.dateFormat', option ? option.value : null)
                        }}
                        id="5ef47629-aa78-4bb6-8720-2dcb62e132dc"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="name" colSpan={2}>
                      {translate('SYSTEM_POLICIES_TIME_FORMAT_INTERFACE')}

                      <Select
                        placeholder={translate('SYSTEM_POLICIES_SELECT_TIME_FORMAT')}
                        className={'font-weight-normal mt5'}
                        isDisabled={!policies.allowPersonalizeClientDateformat}
                        isClearable={false}
                        value={values.userSettings.timeFormat}
                        options={timeFormatsClientOptions}
                        onChange={(option: any) => {
                          setFieldValue('userSettings.timeFormat', option ? option.value : null)
                        }}
                        id="a79ee307-3d54-4716-a383-820a14596915"
                      />

                      <div className={'ml30 mt5'}>
                        <div className="checkbox">
                          <label htmlFor="1f3c920f-2b44-4f2f-a856-fa228de4de2e">
                            <FormikField
                              style={{ margin: '3px 0 0 -20px' }}
                              type="checkbox"
                              disabled={!policies.allowPersonalizeClientDateformat}
                              checked={values.userSettings.timeFormat24h}
                              onChange={(e: any) => {
                                setFieldValue('userSettings.timeFormat24h', e.target.checked)
                              }}
                              id="1f3c920f-2b44-4f2f-a856-fa228de4de2e"
                            />
                            <b>{translate('SYSTEM_POLICIES_24_FORMAT')}</b>
                          </label>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="name pt0 fs14 pb0">{translate('NOTIFICATION_PREFERENCES')}</td>
                    <td className="value"></td>
                  </tr>
                  <tr>
                    <td className="name notification-preferences-column va-top whitespacenowrap">
                      <div>
                        <small className="notification-small-text">{translate('NOTIFICATION_EVENT_TYPE')}</small>
                      </div>
                      {notifSettings.map((item, index) => (
                        <div className="notification-type-name" key={index}>
                          {item.value}
                        </div>
                      ))}
                    </td>
                    <td className="value pt0">
                      <div className="clearfix">
                        <small className="notification-email-notif-header">{translate('EMAIL')}</small>
                        <small className="notification-email-notif-header">{translate('NOTIFICATION_INAPP')}</small>
                      </div>
                      {values.notifSettings.map((item, index) => (
                        <div className="clearfix" key={index}>
                          <InlineCheckboxWrapper>
                            <FormikField name={`notifSettings.${index}.email.enabled`}>
                              {({ field }: any) => (
                                <Form.Check
                                  {...field}
                                  type="checkbox"
                                  checked={values.notifSettings[index].email.enabled}
                                  onChange={(e: any) => {
                                    setFieldValue(`notifSettings.${index}.email.enabled`, e.target.checked)
                                  }}
                                  id={`notifSettings.${index}.email`}
                                />
                              )}
                            </FormikField>
                          </InlineCheckboxWrapper>
                          <InlineCheckboxWrapper>
                            <FormikField name={`notifSettings.${index}.notification.enabled`}>
                              {({ field }: any) => (
                                <Form.Check
                                  {...field}
                                  type="checkbox"
                                  checked={values.notifSettings[index].notification.enabled}
                                  disabled={
                                    !values.notifSettings[index].notification.show ||
                                    values.notifSettings[index].notification.readOnly
                                  }
                                  onChange={(e: any) => {
                                    setFieldValue(`notifSettings.${index}.notification.enabled`, e.target.checked)
                                  }}
                                  id={`notifSettings.${index}.notification`}
                                />
                              )}
                            </FormikField>
                          </InlineCheckboxWrapper>
                        </div>
                      ))}
                    </td>
                  </tr>
                </tbody>
              </Table>
              <Button
                data-action={'save'}
                className="btn btn-primary text-capitalize"
                onClick={() => handleSubmit()}
                disabled={isSaveBtnDisabled(values)}
                id="8bee8fd3-a29c-48a7-9023-e78126dd2be2"
              >
                {translate('SAVE')}
              </Button>
              <Button
                data-action={'cancel'}
                variant="link"
                disabled={isSaveBtnDisabled(values)}
                onClick={() => resetForm()}
                id="157907a0-efbb-4005-a5f0-5d570b7819ad"
              >
                {translate('CANCEL')}
              </Button>
            </FormikForm>
          )
        }}
      </Formik>
    </Loader>
  )
}

export default SettingsForm
